import {
  LogoOmenHorizontal,
  Modal,
  Group,
  Button,
} from "@veneer/core";
import HyperxLogo from "../assets/images/hyper-x.png";
import ZByHpLogo from "../assets/images/z-by-hp.png";
import PolyLogo from "../assets/images/poly-logo.png";
import { useTranslation } from "react-i18next";
import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import "./header.scss";
import { listLocale, Locale } from "../utils/httpclient";
import { getLocaleId } from "../utils/httpclient";
import { getGeoInfo } from "../utils/geoInfo";
import { countries } from "../utils/countries";
import { useDispatch } from "react-redux";
import { setRegionData, setLocaleId } from "../store/regionSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { tracker } from "../utils/interactionTracking";



export const AppHeader = () => {
  const { t } = useTranslation();
  const [selectedLocale, setSelectedLocale] = useState<Locale | null>(null);
  const [showCountriesModal, setShowCountriesModal] = useState(false);
  const [menuEl, setMenuEl] = useState<Element | null>(null);
  const [locales, setLocales] = useState<Locale[]>([]);
  const dispatch = useDispatch();
  const [flag, setFlag] = useState<JSX.Element | undefined>(undefined);
  const countrySelectorRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  const handleCloseModal = () => {
    setShowCountriesModal(false);
    countrySelectorRef.current?.focus();
  };

  const handleCountryChange = (locale: Locale) => {
    setShowCountriesModal(false);
    setSelectedLocale(locale);
    setLocaleId(locale.id);
    dispatch(setLocaleId(locale.id));
    setFlag(countries.find((x) => x.name === locale.country_name)?.flag);
    navigate("/?region=" + `${locale.language}-${locale.country}`);
    countrySelectorRef.current?.focus();
  };

  const navigate = useNavigate();
  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    const fetchData = async () => {
      const [
        listLocalesData,
        defaultLocaleID,
        geographicData,
      ] = await Promise.all([
        listLocale() as Promise<Locale[]>,
        getLocaleId("US", "EN"),
        getGeoInfo(),
      ]);

      const localesArray = listLocalesData.filter((x: any) => !!x.country_name).sort((a, b) => a.country_name.localeCompare(b.country_name));
      setLocales(localesArray);
      // Set default values for regionData
      setLocaleId(defaultLocaleID);
      dispatch(setLocaleId(defaultLocaleID));
      setSelectedLocale(
        localesArray.find((x: any) => x.id === defaultLocaleID) || null
      );
      setFlag(
        countries.find((x) => x.name === "United States")?.flag as
        | JSX.Element
        | undefined
      );
      // Fetch culture code from url query params
      const urlParams = new URLSearchParams(window.location.search);
      const cultureCode = urlParams.get("region");
      if (cultureCode) {
        navigate("/");
      }
      // If cultureCode is not provided, use the one from the geoInfo API
      if (geographicData) {
        setRegionData(geographicData);
        dispatch(setRegionData(geographicData));
        const localeid = await getLocaleId(
          geographicData.countryCode,
          geographicData.languageCode
        );
        if (localeid !== "") {
          setLocaleId(localeid);
          dispatch(setLocaleId(localeid));
          const localeByGeographicData = localesArray.find(
            (x: any) => x.country_name == geographicData!.countryName
          );
          if (geographicData) {
            const countryFlag = countries.find(
              (x) => x.name === geographicData!.countryName
            );
            if (localeByGeographicData && countryFlag) {
              setSelectedLocale(localeByGeographicData);
              setFlag(countryFlag.flag);
            }
          }
        }
      }
      // If cultureCode is provided, use it to set regionData
      // else if (cultureCode) {
      //   if (cultureCode.split("-").length !== 2) {
      //     return;
      //   }
      //   let languageCode = cultureCode.split("-")[0].toUpperCase();
      //   let countryCode = cultureCode.split("-")[1].toUpperCase();
      //   setRegionData({ countryName: "United States", countryCode: countryCode, languageCode: languageCode });
      //   dispatch(setRegionData({ countryName: "United States", countryCode: countryCode, languageCode: languageCode }));
      //   getLocaleId(countryCode, languageCode).then((localeId) => {
      //     if (localeId !== "") {
      //       setLocaleId(localeId);
      //       dispatch(setLocaleId(localeId));
      //       const myLocale = localesArray.find((x: Locale) =>
      //         x.language.toLowerCase() === languageCode.toLowerCase() &&
      //         x.country.toLowerCase() === countryCode.toLowerCase()
      //       );
      //       if (myLocale) {
      //         setSelectedLocale(myLocale);
      //         setFlag(countries.find((x) => x.name === myLocale.country_name)?.flag);
      //       }
      //     }
      //   });
      // }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const el = document.getElementById("menu");
    setMenuEl(el);
  }, []);

  return (
    menuEl && (
      <>
        {createPortal(
          <header>
            <div className="sub-header">
              <div className="container">
                <div className="sub-header-flex">
                  <div className="franchise-list">
                    <a href="https://www.hp.com/us-en/workstations/workstation-pcs.html">
                      <img src={PolyLogo} alt="Poly" data-testid="logo-poly" data-appearance="multicolor" />
                    </a>
                    <a href="https://www.hp.com/us-en/workstations/workstation-pcs.html">
                      <img src={ZByHpLogo} alt="Z By HP" />
                    </a>
                    <a href="https://www.omen.com/us/en.html">
                      <LogoOmenHorizontal
                        appearance="multicolor"
                        alt="Omen"
                        className="logo-omen"
                      />
                    </a>
                    <a href="https://hyperx.com/">
                      <img src={HyperxLogo} alt="HyperX" />
                    </a>
                  </div>
                  {!isAdminRoute && (
                    <div className="lang-site">
                      <span>{t("Country")}:</span>
                      <button
                        ref={countrySelectorRef}
                        className="country-selector-button"
                        onClick={() => {
                          tracker.trackEvent("country_selection", {
                            action: "Country Selection Viewed",
                          });
                          setShowCountriesModal(true);
                        }}
                        aria-label={`Country selector: ${selectedLocale?.country_name || 'Select country'}`}
                      >
                        <span aria-label={`${selectedLocale?.country_name || 'Country'} flag`}>{flag}</span>
                        <span>{selectedLocale?.country_name}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </header>,
          menuEl as Element
        )}
        {!isAdminRoute && (
          <Modal
            align="start"
            id="modal-id"
            className="language-modal"
            show={showCountriesModal}
            style={{ width: "100%" }}
            onClose={handleCloseModal}
            footer={
              <Group>
                <Button onClick={handleCloseModal}>Cancel</Button>
              </Group>
            }
            title={t("SelectCountry")}
          >
            <div className="content">
              <ul id="countrylist" className="countrylist">
                {locales.map((countriesMenuLocale: Locale) => (
                  <li
                    key={countriesMenuLocale.id}
                    className="cursor-pointer"
                    onClick={() => {
                      tracker.trackEvent("country_selection", {
                        action: "Country Changed",
                      });
                      handleCountryChange(countriesMenuLocale);
                    }}
                    onKeyDown={(e) => {
                      tracker.trackEvent("country_selection", {
                        action: "Country Changed",
                      });
                      if (e.key === "Enter" || e.key === " ") {
                        handleCountryChange(countriesMenuLocale);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    {countriesMenuLocale.country_name}
                  </li>
                ))}
              </ul>
            </div>
          </Modal>
        )}
      </>
    )
  );
};
